import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import Table, { IndeterminateCheckbox } from '../../components/common/react-table/Table';
import useManageWorkgroup from './hooks/useManageWorkgroup';
import { IManageWorkGroupTableProps } from './ManageWorkgroup.interface';
import { ConditionalRendering } from '../../components/common/conditional-rendering';

const StyledTable = styled.div`
    margin-top: 3rem;

    th:nth-child(3),
    td:nth-child(3),
    th:nth-child(4),
    td:nth-child(4) {
        text-align: center !important;
    }

    table {
        display: table-caption;
        max-height: 60vh;
        overflow-y: auto;
        width: max-content;
        border-radius: 0;
    }

    thead {
        table-layout: fixed;
        position: sticky;
        top: 0;
        z-index: 10;
    }

    @media (min-width: 1000px) and (max-width: 1200px) {
        overflow-x: auto;
    }

    @media (max-width: 768px) {
        overflow-x: auto;
    }
`;

const StyledTagContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
`;

const StyledTag = styled.span`
    display: inline-block;
    background-color: ${({ theme }) => theme.gray11};
    margin: 0px 8px 16px 0px;
    padding: 7px 14px;
    border-radius: 60px;
    text-align: center;
    border: 1px solid ${({ theme }) => theme.gray9};
    max-width: 100px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    cursor: pointer;
`;

const StyledAnchor = styled.a`
    text-decoration: underline;
    display: inline-block;
    max-width: 23rem;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    vertical-align: bottom;
`;

const StyledNoRecord = styled.div`
    margin: 0 0.5rem;
`;

const ManageWorkgroupTable: React.FC<IManageWorkGroupTableProps> = ({
    workgroups,
    defaultWorkgroups,
    onToggleDefault,
}) => {
    const { redirectToTicketManagequeue, redirectToEditWorkgroupTag } = useManageWorkgroup();
    const intl = useIntl();

    const columns = useMemo(
        () => [
            {
                Header: 'ticket_management.form_header.workgroup',
                accessor: 'workGroupName',
                width: '23rem',
                Cell: ({ row }) => {
                    return (
                        <StyledAnchor href="#" onClick={event => redirectToEditWorkgroupTag(event, row.original.id)} title= {row.values.workGroupName}>
                            {row.values.workGroupName}
                        </StyledAnchor>
                    );
                },
            },
            {
                Header: () => (
                    <span className="usa-sr-only">
                        <FormattedMessage id="ticket_management.manage_workgroup.column.assigneeCount" />
                    </span>
                ),
                accessor: 'assigneeCount',
                Cell: ({ row }) => {
                    return (
                        <a href="#" onClick={event => redirectToTicketManagequeue(event, row.values.workGroupName)}>
                            ({row.values.assigneeCount})
                        </a>
                    );
                },
            },
            {
                Header: 'ticket_management.table.workgroup.column2.isDefault',
                accessor: 'defaultWorkGroup',
                Cell: ({ row }) => {
                    return (
                        <IndeterminateCheckbox
                            isChecked={defaultWorkgroups[row.original.id]}
                            onChange={() => onToggleDefault(row.original.id)}
                            aria-label={`${row.original.workGroupName} ${intl.formatMessage({ id: 'ticket_management.table.workgroup.column2.isDefault' })}`}
                        />
                    );
                },
            },
            {
                Header: 'ticket_management.table.workgroup.column3.tags',
                accessor: 'tags',
                width: '25rem',
                Cell: ({ row }) => {
                    return (
                        <StyledTagContainer>
                            {row.values.tags.map(tag => (
                                <StyledTag key={tag.id} data-testid="tag-name" title={tag.name}>
                                    {tag.name}
                                </StyledTag>
                            ))}
                        </StyledTagContainer>
                    );
                },
            },
        ],
        [defaultWorkgroups, onToggleDefault, redirectToTicketManagequeue],
    );

    return (
        <StyledTable data-testid='manage-workgroup-table'>
            <Table
                id="mange_workgroup_table"
                className="workgroup-table"
                columns={columns}
                data={workgroups}
                variant="simple"
                hasBorders={true}
            />
            <ConditionalRendering isTrue={workgroups.length === 0}>
                <StyledNoRecord>
                    <FormattedMessage id="ticket_management.table.workgroup.column4.noRecords" />
                </StyledNoRecord>
            </ConditionalRendering>
        </StyledTable>
    );
};

export default ManageWorkgroupTable;